import { useEffect, useRef, useState } from 'react';
import SafeIcon from './SafeIcon';
import MaterialPicker, { type PickedItem } from './MaterialPicker';
import { useApp } from '../i18n/AppContext';
import type { Post } from '../types';

export default function RequestChangesDrawer({
  post,
  siblings,
  onClose,
  onSend,
}: {
  post: Post;
  siblings: Post[];
  onClose: () => void;
  onSend: (text: string, ids: string[], materials: { name: string; src?: string }[]) => void;
}) {
  const { translate, readOnlyProps } = useApp();
  const editableSiblings = siblings.filter((item) =>
    ['Needs your review', 'Scheduled'].includes(item.status)
  );
  const versionOptions = editableSiblings.length ? editableSiblings : [post];
  const [selected, setSelected] = useState<string[]>([post.id]);
  const [feedback, setFeedback] = useState('');
  const [materials, setMaterials] = useState<PickedItem[]>([]);
  const [isPickingMaterial, setIsPickingMaterial] = useState(false);
  const feedbackFieldRef = useRef<HTMLTextAreaElement>(null);
  const latestOnCloseRef = useRef(onClose);
  latestOnCloseRef.current = onClose;

  useEffect(() => {
    const elementThatOpenedDrawer = document.activeElement as HTMLElement | null;
    feedbackFieldRef.current?.focus();
    const closeOnEscapeUnlessPickerOpen = (event: KeyboardEvent) => {
      const materialPickerIsOpen = !!document.querySelector('.material-picker');
      if (event.key === 'Escape' && !materialPickerIsOpen) latestOnCloseRef.current();
    };
    document.addEventListener('keydown', closeOnEscapeUnlessPickerOpen);
    return function restoreFocusToOpener() {
      document.removeEventListener('keydown', closeOnEscapeUnlessPickerOpen);
      (document.getElementById('review-request-changes') ?? elementThatOpenedDrawer)?.focus();
    };
  }, []);

  const toggleVersion = (id: string) =>
    setSelected(selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id]);
  const selectedChannelNames = versionOptions
    .filter((item) => selected.includes(item.id))
    .map((item) => item.channel)
    .join(translate(' and '));
  const request = feedback.trim();
  const bannerMessage = () => {
    if (selected.length === 0) return translate('Choose at least one version.');
    if (request)
      return translate('AMY will update the {v} versions: “{request}”', {
        v: selectedChannelNames,
        request,
      });
    return translate('Write what to change. AMY will update the {v} versions.', {
      v: selectedChannelNames,
    });
  };

  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-changes-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="request-changes-title">{translate('Request changes')}</h2>
            <div className="muted">{post.title}</div>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        <label htmlFor="feedback">{translate('Your feedback')}</label>
        <textarea
          ref={feedbackFieldRef}
          id="feedback"
          className="text-field"
          rows={5}
          value={feedback}
          onChange={(event) => setFeedback(event.target.value)}
          placeholder={translate('For example: brighten the photo and keep the caption as it is.')}
          {...readOnlyProps}
        />
        {versionOptions.length > 1 && (
          <>
            <h3 style={{ marginTop: 20 }}>{translate('Apply to versions')}</h3>
            <div className="filter-row">
              {versionOptions.map((item) => (
                <button
                  key={item.id}
                  className={`version-chip ${selected.includes(item.id) ? 'selected' : ''}`}
                  aria-pressed={selected.includes(item.id)}
                  onClick={() => toggleVersion(item.id)}
                >
                  {item.channel}
                </button>
              ))}
            </div>
          </>
        )}
        <div className="notice-banner" style={{ marginTop: 18 }}>
          <SafeIcon name="FiInfo" />
          <span>{bannerMessage()}</span>
        </div>
        <p className="muted small-print">
          {translate('Other versions keep their approval and schedule.')}
        </p>
        {materials.length > 0 && (
          <div className="chat-attachments">
            {materials.map((item) => (
              <span key={item.source + item.file} className="attachment-chip">
                {item.src ? (
                  <img src={item.src} alt="" />
                ) : (
                  <SafeIcon name="FiPaperclip" size={12} />
                )}
                {item.file}
                <button
                  aria-label={translate('Remove {word}', { word: item.file })}
                  onClick={() => setMaterials(materials.filter((material) => material !== item))}
                >
                  <SafeIcon name="FiX" size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        <button className="button" onClick={() => setIsPickingMaterial(true)} {...readOnlyProps}>
          <SafeIcon name="FiPaperclip" />
          {translate(materials.length ? 'Add more material' : 'Add material')}
        </button>
        {materials.length > 0 && (
          <p className="muted small-print">
            {translate('AMY uses these files for the new version.')}
          </p>
        )}
        <div style={{ marginTop: 24 }}>
          <button
            className="button primary"
            style={{ width: '100%' }}
            onClick={() =>
              onSend(
                request,
                selected,
                materials.map((item) => ({ name: item.name, src: item.src }))
              )
            }
            disabled={readOnlyProps.disabled || !request || selected.length === 0}
            title={readOnlyProps.title}
          >
            <SafeIcon name="FiSend" />
            {translate('Send to AMY')}
          </button>
        </div>
      </section>
      {isPickingMaterial && (
        <MaterialPicker
          title="Choose material for this change"
          multiple
          onCancel={() => setIsPickingMaterial(false)}
          onPick={(items) => {
            setMaterials([
              ...materials,
              ...items.filter((item) => !materials.some((material) => material.file === item.file)),
            ]);
            setIsPickingMaterial(false);
          }}
        />
      )}
    </div>
  );
}
